import { AlphaColumnBase } from '../../reducers/AlphasReducer/AlphasReducer.types';
import { AlphaTabType } from '../constants';

const selectedWeightColumn: AlphaColumnBase = {
  id: 'weight',
  parent: 'selected',
  name: 'Selected Weight',
  active: true,
  category: 'Summary',
  display: true,
  type: 'decimal',
};

export const allColumnsParents = ['is', 'os', 'settings', 'selected'];
const dateSubmittedColumn: AlphaColumnBase = {
  id: 'dateSubmitted',
  name: 'Date Submitted (EST)',
  active: true,
  category: 'Summary',
  display: true,
  type: 'datetime',
};

const dateCreatedColumn: AlphaColumnBase = {
  id: 'dateCreated',
  name: 'Date Created (EST)',
  active: true,
  category: 'Summary',
  display: true,
  type: 'datetime',
};

const osmosisPointsColumn: AlphaColumnBase = {
  id: 'osmosisPoints',
  name: 'Osmosis Points',
  active: true,
  category: 'Properties',
  activeTabsWithoutParent: ['submitted'],
  display: true,
  type: 'integer',
};

export const initialSelectedColumns = (tabType?: string, permissions?: string[]): AlphaColumnBase[] => {
  const isConsultant = permissions?.includes('CONSULTANT') || false;
  const columns: AlphaColumnBase[] = [
    {
      id: 'favorite',
      name: 'Favorite',
      active: true,
      category: 'Summary',
      iconColumn: true,
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'alphaId',
      name: 'ID',
      active: true,
      category: 'Summary',
      display: true,
      type: 'string',
      width: 110,
    },
    {
      id: 'name',
      name: 'Name',
      active: true,
      category: 'Summary',
      filterOperator: '~',
      display: true,
      type: 'string',
    },
    {
      id: 'competitions',
      name: 'Competition',
      active: true,
      category: 'Summary',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'type',
      name: 'Type',
      active: true,
      category: 'Summary',
      filterOperator: '=',
      display: true,
      type: 'choices',
      width: 80,
    },
    {
      id: 'status',
      name: 'Status',
      active: true,
      category: 'Properties',
      filterOperator: '=',
      display: true,
      type: 'choices',
      width: 130,
    },
    {
      id: 'language',
      parent: 'settings',
      name: 'Language',
      active: true,
      category: 'Summary',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    ...(tabType === AlphaTabType.SUBMITTED ? [dateSubmittedColumn] : [dateCreatedColumn]),
    ...(isConsultant ? [osmosisPointsColumn] : []),
    {
      id: 'region',
      parent: 'settings',
      name: 'Region',
      active: true,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
      width: 80,
    },
    {
      id: 'instrumentType',
      parent: 'settings',
      name: 'Instrument Type',
      active: false,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'universe',
      parent: 'settings',
      name: 'Universe',
      active: true,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'delay',
      parent: 'settings',
      name: 'Delay',
      active: false,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'decay',
      parent: 'settings',
      name: 'Decay',
      active: false,
      category: 'Settings',
      display: true,
      type: 'integer',
    },
    {
      id: 'neutralization',
      parent: 'settings',
      name: 'Neutralization',
      active: false,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'truncation',
      parent: 'settings',
      name: 'Truncation',
      active: false,
      category: 'Settings',
      display: true,
      type: 'decimal',
    },
    {
      id: 'lookback',
      parent: 'settings',
      name: 'Lookback',
      active: false,
      category: 'Settings',
      display: true,
      type: 'integer',
    },
    {
      id: 'pasteurization',
      parent: 'settings',
      name: 'Pasteurization',
      active: false,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'maxTrade',
      parent: 'settings',
      name: 'Max Trade',
      active: false,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'maxPosition',
      parent: 'settings',
      name: 'Max Position',
      active: false,
      category: 'Settings',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'sharpe',
      parent: 'is',
      name: 'Sharpe',
      active: true,
      category: 'Performance',
      possibleParents: ['is', 'os'],
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'decimal',
      width: 80,
    },
    {
      id: 'returns',
      parent: 'is',
      name: 'Returns',
      active: true,
      category: 'Performance',
      possibleParents: ['is', 'os'],
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'percent',
      width: 80,
    },
    {
      id: 'pnl',
      parent: 'is',
      name: 'PnL',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'amount',
    },
    {
      id: 'turnover',
      parent: 'is',
      name: 'Turnover',
      active: true,
      category: 'Performance',
      possibleParents: ['is', 'os'],
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'percent',
      width: 80,
    },
    {
      id: 'drawdown',
      parent: 'is',
      name: 'Drawdown',
      active: false,
      category: 'Performance',
      possibleParents: ['is', 'os'],
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'percent',
    },
    {
      id: 'margin',
      parent: 'is',
      name: 'Margin',
      active: false,
      category: 'Performance',
      possibleParents: ['is', 'os'],
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'permyriad',
    },
    {
      id: 'fitness',
      parent: 'is',
      name: 'Fitness',
      active: false,
      category: 'Performance',
      possibleParents: ['is', 'os'],
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'decimal',
    },
    {
      id: 'correlation',
      name: 'Self Corr',
      active: false,
      category: 'Performance',
      display: false,
      type: 'integer',
      tab: ['alphaTag'],
    },
    {
      id: 'category',
      name: 'Category',
      active: false,
      category: 'Properties',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'color',
      name: 'Color',
      active: false,
      category: 'Properties',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'tags',
      name: 'Tag',
      active: true,
      category: 'Properties',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'hidden',
      name: 'Hidden',
      iconColumn: true,
      active: false,
      category: 'Properties',
      filterOperator: '=',
      display: true,
      type: 'choices',
    },
    {
      id: 'bookSize',
      parent: 'is',
      name: 'Book Size',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'amount',
    },
    {
      id: 'longCount',
      parent: 'is',
      name: 'Long Count',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'integer',
    },
    {
      id: 'shortCount',
      parent: 'is',
      name: 'Short Count',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['unsubmitted', 'submitted'],
      display: true,
      type: 'integer',
    },
    ...(tabType === AlphaTabType.SUBMITTED
      ? [{ ...dateCreatedColumn, display: false, active: false }]
      : tabType === AlphaTabType.UNSUBMITTED
      ? []
      : [dateSubmittedColumn]),
    {
      id: 'startDate',
      parent: 'os',
      name: 'Start Date (EST)',
      active: false,
      category: 'Summary',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'datetime',
    },
    {
      id: 'sharpe60',
      parent: 'os',
      name: 'Sharpe 60',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'sharpe125',
      parent: 'os',
      name: 'Sharpe 125',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'sharpe250',
      parent: 'os',
      name: 'Sharpe 250',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'sharpe500',
      parent: 'os',
      name: 'Sharpe 500',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'osISSharpeRatio',
      parent: 'os',
      name: 'OS/IS Ratio',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'preCloseSharpe',
      parent: 'os',
      name: 'Pre-Close Sharpe',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'preCloseSharpeRatio',
      parent: 'os',
      name: 'Pre-Close Ratio',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'selfCorrelation',
      parent: 'is',
      name: 'Self-Correlation',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
    {
      id: 'prodCorrelation',
      parent: 'is',
      name: 'Prod-Correlation',
      active: false,
      category: 'Performance',
      activeTabsWithoutParent: ['submitted'],
      display: false,
      type: 'decimal',
    },
  ];

  if (tabType && tabType === AlphaTabType.SUPER_SELECTION) {
    columns.push(selectedWeightColumn);
  }

  return columns;
};
